import { AnimatePresence, m } from 'motion/react';
import { X } from 'lucide-react';
import { useId, useRef } from 'react';
import type { ReactNode } from 'react';

import { useOverlayChrome } from './useOverlayChrome';
import { useT } from '../i18n/provider';
import { cn } from '../lib/cn';

/**
 * El diálogo del sistema.
 *
 * Antes solo existía `ConfirmByName`, con su propia capa fija y su propio
 * montaje instantáneo; cualquier otra pantalla que necesitara un diálogo lo
 * habría vuelto a escribir. Aquí se decide una vez: el fondo se funde y el
 * panel entra con muelle desde abajo. Lo que no se ve pero es lo que lo hace
 * usable —foco, bloqueo del fondo y Escape— está en `useOverlayChrome`, que
 * comparte con el visor de ejecución a pantalla completa.
 *
 * No usa `<dialog>` nativo porque su `::backdrop` no admite `backdrop-filter`
 * en todos los motores que este producto soporta, y el desenfoque es lo que
 * separa el diálogo de la pantalla que hay detrás.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const t = useT();
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);

  useOverlayChrome(open, onClose, panel);

  const width = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl' }[size];

  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-90 flex items-end justify-center p-0 sm:items-center sm:p-4">
          <m.button
            type="button"
            data-dismiss
            aria-label={t('common.close')}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            className="absolute inset-0 bg-black/45 backdrop-blur-xs"
          />

          <m.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${id}-title`}
            aria-describedby={description ? `${id}-description` : undefined}
            tabIndex={-1}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98, transition: { duration: 0.14 } }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className={cn(
              /*
                El límite de altura va aquí, en el diálogo entero, y no solo en
                su cuerpo.

                Estaba en el cuerpo —`max-h-[70vh]`— sin contar la cabecera ni
                el pie, que suman otros 160 píxeles largos. En una pantalla baja
                eso empujaba la cabecera fuera de la ventana, y como el panel
                está anclado abajo en móvil, lo que se perdía era el título. Con
                el tope en el panel y el `flex` de abajo, lo único que desplaza
                es el contenido.

                `dvh` y no `vh` porque en móvil la barra de direcciones entra y
                sale, y `vh` mide la ventana como si nunca estuviera.
              */
              'relative flex max-h-[92dvh] w-full flex-col rounded-t-xl border border-line bg-paper shadow-lg outline-none sm:max-h-[85dvh] sm:rounded-xl',
              width,
            )}
          >
            <header className="flex shrink-0 items-start gap-4 border-b border-line px-6 py-5">
              <div className="min-w-0 grow">
                <h2 id={`${id}-title`} className="text-lg font-semibold tracking-tight text-ink">
                  {title}
                </h2>
                {description ? (
                  <p id={`${id}-description`} className="mt-1.5 text-sm text-ink-soft">
                    {description}
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                data-dismiss
                onClick={onClose}
                aria-label={t('common.close')}
                className="-m-1 shrink-0 rounded-sm p-1.5 text-ink-faint transition-colors hover:bg-muted hover:text-ink"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </header>

            <div className="min-h-0 grow overflow-y-auto px-6 py-5">{children}</div>

            {footer ? (
              <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-line px-6 py-4">
                {footer}
              </footer>
            ) : null}
          </m.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
